import type { SelectedFilterOption } from 'features/charts-v2/types'
import type {
  FilterFieldRestriction,
  FilterRestriction,
} from 'features/charts-v2/types/filter-restriction'
import type { InheritedFilters } from 'features/charts-v2/types/inherited-filters'

// Same sentinel `FilterSelectionPopover.tsx` and `FilterCard.tsx` each
// redeclare locally for the "includeNull" encoding — a Rating filter with
// null-score rows included carries this literal in its `values` array
// alongside (or instead of) discrete star values.
const NULL_VALUE = 'Null'

const BOTH_VALUE_TYPE = 0

/** One global selection → the restriction it imposes on a tile's picker for
 *  the same field, or `null` when the selection carries no constraint (an
 *  empty value list, or an operator v1 can never emit — see the doc comment
 *  on `deriveFilterRestriction`). */
export function restrictionForSelection(
  selection: SelectedFilterOption,
): FilterFieldRestriction | null {
  // Window/range selection — today only Rating (score / post_score) reaches
  // here, since a v1 dashboard global can express min/max/valueType for it.
  if (selection.min !== undefined || selection.max !== undefined) {
    return {
      kind: 'range',
      min: selection.min,
      max: selection.max,
      allowedValueTypes:
        selection.valueType === undefined ||
        selection.valueType === BOTH_VALUE_TYPE
          ? undefined
          : [selection.valueType],
      allowNull: selection.values.includes(NULL_VALUE),
    }
  }

  // Numeric-operator selection (Records / Sentences / Avg Sentiment). A
  // dashboards-v2-1 global CAN carry one since the v1→v2 filter-state
  // migration (its old v1 storage couldn't — see AGENTS.md); a `/charts`
  // page or Feedback/Keywords global still can't, per those features' own
  // v1↔v2 boundary (`translate-v2-to-v1-filters.ts`'s `convertFilterOption`).
  if (selection.numericValue !== undefined) {
    switch (selection.operator) {
      case 'greater than':
      case 'greater than or equal to':
        return { kind: 'range', min: selection.numericValue }
      case 'less than':
      case 'less than or equal to':
        return { kind: 'range', max: selection.numericValue }
      case 'equal to':
        return {
          kind: 'range',
          min: selection.numericValue,
          max: selection.numericValue,
        }
      default:
        // 'not equal to' has no range representation — unconstrained.
        return null
    }
  }

  // Select/facet selection.
  if (selection.operator === 'is' && selection.values.length > 0) {
    return { kind: 'values', allowed: new Set(selection.values) }
  }

  // An empty selection carries no constraint (an unfiltered global permits
  // all values), and 'is not' has no subset representation to restrict a
  // picker to — both fall through to "unrestricted".
  return null
}

/**
 * Derives what a tile's filter picker may select from the dashboard's global
 * filters, so a tile can only narrow — never broaden past — what the
 * dashboard already filters to. Built from the SAME `InheritedFilters` the
 * sidebar already renders as struck-through "superseded" cards
 * (`deriveParentFilterInheritance` / `deriveParentFacetInheritance`), so
 * enforcement can't drift from display — never construct a `FilterRestriction`
 * ad hoc. Deliberately reads every field regardless of
 * `entry.supersededNote`: the restriction is drawn from the GLOBAL's own
 * selection for that field, not from whether the tile has already overridden
 * it — a field the tile currently overrides still constrains the tile's
 * *next* edit to that field.
 *
 * Returns `undefined` — meaning "no restriction, offer every project value"
 * — in two cases: no parent scope at all (a standalone `/charts` page, or
 * Feedback/Keywords, which never pass `inherited`), or `isUnlinked` (a widget
 * tile deliberately desynced from the dashboard's filters — see
 * `useEmbeddableFilters`'s `toggleUnlinked`). Chart tiles have no `isUnlinked`
 * concept yet (see the TODO in `mergeParentFiltersIntoPanel`), so `opts` is
 * only ever passed by the widget-tile builder.
 */
export function deriveFilterRestriction(
  inherited: InheritedFilters | undefined,
  opts?: { isUnlinked?: boolean },
): FilterRestriction | undefined {
  if (!inherited || opts?.isUnlinked) return undefined

  const byField = new Map<string, FilterFieldRestriction>()
  for (const entry of inherited.facets) {
    const restriction = restrictionForSelection(entry.selection)
    if (restriction) byField.set(entry.selection.field, restriction)
  }

  return { byField, sourceLabel: inherited.sourceLabel }
}
